import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Button, Form, type FormProps, Input, message, Modal } from 'antd';
import { renameFolder } from '@/services/api/folderService';
import { renameDocument } from '@/services/api/documentService';
import { useTranslation } from 'react-i18next';
import { resolveNodeId } from '@/utils/helpers';

type FieldType = {
  filename?: string;
};

interface EditFileNameProps {
  node: any;
  setNode: (node: any) => void;
  onLoadData: any;
  open: boolean;
  onOk: () => void;
  onCancel: () => void;
}

const ModalEditFileName: FC<EditFileNameProps> = ({
  node,
  setNode,
  onLoadData,
  open,
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<any>(false);

  useEffect(() => {
    if (open && node) {
      form.setFieldValue('filename', node?.name);
    }
  }, [node, open, form]);

  const onFinish: FormProps<FieldType>['onFinish'] = async values => {
    setIsLoading(true);
    let response;
    const nodeId = resolveNodeId(node);
    if (!nodeId) {
      setIsLoading(false);
      return;
    }
    if (node.isLeaf) {
      response = await renameDocument({
        document_id: nodeId,
        name: values.filename,
      });
    } else {
      response = await renameFolder({
        folder_id: nodeId,
        folder_name: values.filename,
      });
    }
    if (!response?.success) {
      message.error(response?.error || t('home.documents.modals.edit.error'));
      setIsLoading(false);
      return;
    }
    message.success(t('home.documents.modals.edit.success'));
    onLoadData({
      ...node,
      name: values.filename,
      path: response?.data?.path,
    });
    setIsLoading(false);
    onCancel();
  };

  return (
    <div>
      <Modal
        title={t('home.documents.modals.edit.title')}
        open={open}
        destroyOnClose
        footer={[
          <Button
            key="update"
            type="primary"
            onClick={() => form.submit()}
            loading={isLoading}
          >
            {t('home.documents.modals.edit.confirm')}
          </Button>,
        ]}
        onOk={onOk}
        onCancel={onCancel}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            name="filename"
            label={t('home.documents.modals.edit.label')}
            required={false}
            rules={[
              {
                required: true,
                message: t('home.documents.modals.edit.requiredMessage', {
                  type: node?.isLeaf ? 'file' : 'folder',
                }),
              },
              {
                pattern: /^[^\/\\:\*\?"<>\|]+$/,
                message: t('home.documents.modals.edit.invalid', {
                  type: node?.isLeaf ? 'file' : 'folder',
                }),
              },
            ]}
          >
            <Input
              placeholder={t('home.documents.modals.edit.placeholder', {
                type: node?.isLeaf ? 'file' : 'folder',
              })}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModalEditFileName;
